'use client';

import type { Facet } from '@vs-shop/engine/capability/productListing/facetSet';
import { FilterRangeUnit } from '@vs-shop/commerce-ui/filterRange';
import { computeStepFromValues } from '@vs-shop/domain/price/computeStep';
import { decodePriceOrZero, Price as makePrice } from '@vs-shop/schemas/primitive/price';
import {
  makeContinuousRangeValue,
  makePriceRangeValue,
  makeSteppedRangeValue,
} from '@vs-shop/schemas/product/filter/state/range';
import { BigDecimal, Chunk, Match, Predicate } from 'effect';

import { getCurrencyStep, getCurrencySymbol, getDefaultCurrency } from '../../../config/currency';
import { SteppedRangeConfig } from '../../../config/filterSlider';
import { FilterRangeControl } from '../filter/FilterRangeControl';

type Props = {
  readonly facet: Extract<Facet, { readonly _tag: 'Range' }>;
  readonly selected: { readonly min: number | null; readonly max: number | null };
  readonly onChange: (min: number | null, max: number | null) => void;
};

/** Adapts listing values to RCProfi's established range controls. */
export const ListingRangeFilter = ({ facet, selected, onChange }: Props) => {
  const price = Predicate.isTagged(facet.key, 'Price');
  const currency = getDefaultCurrency();
  const min = price
    ? BigDecimal.floor(decodePriceOrZero(facet.bounds.min))
    : BigDecimal.fromNumberUnsafe(facet.bounds.min);
  const max = price
    ? BigDecimal.ceil(decodePriceOrZero(facet.bounds.max))
    : BigDecimal.fromNumberUnsafe(facet.bounds.max);
  const values = Chunk.fromIterable(facet.values.toSorted((left, right) => left - right));
  const rangeValue = price
    ? makePriceRangeValue({ min: makePrice(min), max: makePrice(max) })
    : Chunk.size(values) >= 2 && Chunk.size(values) <= SteppedRangeConfig.MAX_STEPS
      ? makeSteppedRangeValue({ min, max, steps: Chunk.map(values, BigDecimal.fromNumberUnsafe) })
      : makeContinuousRangeValue({ min, max });
  const step = price
    ? getCurrencyStep(currency)
    : computeStepFromValues(
        values,
        BigDecimal.fromNumberUnsafe(facet.valueType === 'integer' ? 1 : 0.1)
      );
  const decode = (value: number) =>
    price ? decodePriceOrZero(value) : BigDecimal.fromNumberUnsafe(value);
  const encode = (value: BigDecimal.BigDecimal) =>
    price ? Math.round(BigDecimal.toNumberUnsafe(value) * 100) : BigDecimal.toNumberUnsafe(value);

  return (
    <FilterRangeControl
      rangeValue={rangeValue}
      selectedMin={selected.min === null ? min : decode(selected.min)}
      selectedMax={selected.max === null ? max : decode(selected.max)}
      step={step}
      unit={Match.value(facet).pipe(
        Match.when({ key: Predicate.isTagged('Price') }, () => (
          <FilterRangeUnit>{getCurrencySymbol(currency)}</FilterRangeUnit>
        )),
        Match.when({ unit: Predicate.isString }, ({ unit }) => (
          <FilterRangeUnit>{unit}</FilterRangeUnit>
        )),
        Match.orElse(() => null)
      )}
      onValueChange={(value) =>
        onChange(
          BigDecimal.equals(value.min, min) ? null : encode(value.min),
          BigDecimal.equals(value.max, max) ? null : encode(value.max)
        )
      }
    />
  );
};
